import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Shield, FileText, Wrench, ClipboardList, CheckCircle, Award, AlertTriangle, BookOpen } from "lucide-react";
import { MarketingNav, MarketingFooter } from "./MarketingLayout";
import "../../marketing.css";

const MODULES = [
  { icon: Shield, name: "FRMS Phase 1 — FDTL Monitor", desc: "Fatigue status board, FDP/rest validation, cumulative FTL tracking and Do Not Assign alerts. The foundation of regulatory compliance.", badge: "PHASE 1", color: "text-orange-400 bg-orange-500/10 border-orange-500/20" },
  { icon: Shield, name: "FRMS Phase 2 — Predictive", desc: "Bio-mathematical fatigue modelling, circadian rhythm scoring, 7-day pattern detection and AI crew recommendations.", badge: "PHASE 2", color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  { icon: Award, name: "FRMS Phase 3 — Full FRMS", desc: "Scientific evidence library, hazard register, FRMS policy documentation and DGCA audit-ready reporting package.", badge: "PHASE 3", color: "text-green-400 bg-green-500/10 border-green-500/20" },
  { icon: Wrench, name: "Engineering Risk", desc: "Technical risk scoring for maintenance deferrals, MEL items and engineering safety events integrated with crew scheduling.", badge: "CAMO", color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  { icon: ClipboardList, name: "CAMO Planning", desc: "Continuing airworthiness task planning, component expiry tracking and maintenance release integration with the ops schedule.", badge: "AIRWORTHY", color: "text-sky-400 bg-sky-500/10 border-sky-500/20" },
  { icon: AlertTriangle, name: "Safety SMS", desc: "DGCA SDMS-compatible incident and hazard reporting, risk matrix visualisation and corrective action tracking.", badge: "SMS", color: "text-red-400 bg-red-500/10 border-red-500/20" },
  { icon: FileText, name: "Audit Log", desc: "Immutable audit trail for every roster change, FDP override, pairing build and compliance decision with user attribution.", badge: "AUDIT", color: "text-violet-400 bg-violet-500/10 border-violet-500/20" },
  { icon: BookOpen, name: "Operations Manual", desc: "Digital operations manual with version control, chapter-level change tracking and distribution confirmation recording.", badge: "DOC CTRL", color: "text-slate-400 bg-slate-500/10 border-slate-500/20" },
];

const FRMS_JOURNEY = [
  {
    phase: "Phase 1",
    title: "Prescriptive FDTL Compliance",
    timeline: "Month 1–3 · Mandatory Nov 2025",
    items: [
      "Day FDP ≤13h / Night FDP ≤10h (WOCL reporting, new Nov 2025)",
      "Rest = max(12h, preceding duty); post-WOCL: 24h + local night + 2h buffer",
      "48 consecutive hours rest within any 168-hour rolling window (new Nov 2025)",
      "Maximum 2 consecutive night duties per 7-day period (reduced from 4, Nov 2025)",
      "Weekly flight time ≤35h; 28-day ≤100/125h; Annual ≤1,000h flight / ≤1,800h duty",
      "FDP extension ban during split duty (Jan 2024, enforced Nov 2025)",
    ],
    color: "border-orange-500/20 bg-orange-500/5",
    badge: "text-orange-400",
  },
  {
    phase: "Phase 2",
    title: "Predictive Risk Management",
    timeline: "Month 4–9 · Voluntary",
    items: [
      "Bio-mathematical fatigue score (SAFE / FAST model implementation)",
      "Circadian rhythm and sleep pressure modelling per crew member",
      "7-day fatigue pattern detection and early warning alerts",
      "AI crew recommendation engine (Claude Sonnet — suggests least-fatigued crew)",
      "Trend evidence reports for DGCA FRMS Phase 2 progression",
    ],
    color: "border-amber-500/20 bg-amber-500/5",
    badge: "text-amber-400",
  },
  {
    phase: "Phase 3",
    title: "Full FRMS Certification",
    timeline: "Month 10+ · Voluntary",
    items: [
      "Scientific evidence library with peer-reviewed fatigue research references",
      "Hazard register with corrective action workflow and closure tracking",
      "FRMS policy and procedure documentation with version control",
      "DGCA audit package — all evidence auto-compiled from platform activity",
      "Continuous improvement loop integrated with SMS incident data",
    ],
    color: "border-green-500/20 bg-green-500/5",
    badge: "text-green-400",
  },
];

export default function CompliancePage() {
  return (
    <div className="min-h-screen bg-[#07111f] text-white marketing-font-body">
      <MarketingNav />

      {/* ── HERO ── */}
      <section className="pt-28 pb-20 relative overflow-hidden grid-texture">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 50% at 60% 30%, rgba(34,197,94,0.07) 0%, transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 px-3 py-1.5 rounded mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 live-dot" />
              <span className="text-[10px] text-green-400 marketing-font-mono uppercase font-bold tracking-widest">Compliance & FRMS — Nov 2025</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-black marketing-font-heading leading-tight mb-5">
              DGCA CAR-OPS Nov 2025.<br />
              <span className="text-green-400">Built into the platform, not bolted on.</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed mb-8 max-w-2xl">
              A structured three-phase FRMS journey from basic FDTL compliance to full scientific risk management — with CAMO, Engineering, Safety SMS and an immutable Audit Log.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/marketing/demo" className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5" data-testid="compliance-demo-btn">
                Request Demo <ArrowRight size={16} />
              </Link>
              <Link to="/login" className="flex items-center gap-2 border border-white/15 hover:border-white/30 text-slate-300 px-7 py-3.5 transition-all">
                Explore Platform <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── FRMS 3-PHASE JOURNEY ── */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="text-[10px] text-green-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">FRMS Journey</div>
            <h2 className="text-3xl font-black marketing-font-heading">Three phases. One structured path to full FRMS.</h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            {FRMS_JOURNEY.map((p, i) => (
              <div key={i} className={`border ${p.color.split(" ").find(c => c.startsWith("border"))} ${p.color.split(" ").find(c => c.startsWith("bg"))} p-6`}>
                <div className="flex items-center gap-3 mb-1">
                  <span className={`text-[10px] marketing-font-mono font-black uppercase border border-current/30 px-2 py-1 ${p.badge}`}>{p.phase}</span>
                  <span className="text-[10px] text-slate-600 marketing-font-mono">{p.timeline}</span>
                </div>
                <div className="font-bold text-white marketing-font-heading mb-4">{p.title}</div>
                <div className="space-y-2">
                  {p.items.map((item, j) => (
                    <div key={j} className="flex items-start gap-2 text-xs text-slate-400">
                      <CheckCircle size={12} className={`${p.badge} shrink-0 mt-0.5`} /> {item}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 8 MODULES ── */}
      <section className="py-20 border-t border-white/5 bg-[#04090f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-10">
            <div className="text-[10px] text-green-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">Compliance suite</div>
            <h2 className="text-3xl font-black marketing-font-heading">Every regulatory module, connected.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              const [textColor, bgColor, borderColor] = m.color.split(" ");
              return (
                <div key={i} className={`bg-[#0c1929]/50 border ${borderColor} hover:border-opacity-60 p-5 card-glow transition-all`}>
                  <div className={`w-8 h-8 ${bgColor} border ${borderColor} flex items-center justify-center mb-3`}>
                    <Icon size={14} className={textColor} />
                  </div>
                  <div className={`text-[9px] marketing-font-mono font-bold uppercase tracking-widest mb-1 ${textColor}`}>{m.badge}</div>
                  <div className="text-sm font-bold text-white marketing-font-heading mb-2">{m.name.split(" — ")[0]}</div>
                  <p className="text-[11px] text-slate-600 leading-relaxed">{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── DGCA SPECIFIC ── */}
      <section className="py-20 border-t border-white/5 grid-texture">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-[10px] text-green-400 marketing-font-mono uppercase tracking-widest font-bold mb-4">DGCA Specific</div>
              <h2 className="text-3xl font-black marketing-font-heading mb-5">
                Built for DGCA.<br /><span className="text-green-400">Not adapted from foreign frameworks.</span>
              </h2>
              <p className="text-slate-400 leading-relaxed mb-8">Most crew management platforms are built for FAA or EASA and then adapted for India. Fatigue Shield was designed from scratch for DGCA CAR-OPS (Jan 2024) with the November 2025 enforcement fully built in.</p>
              <div className="space-y-3">
                {[
                  "DGCA CAR-OPS Jan 2024 — fully enforced Nov 1, 2025",
                  "Day FDP ≤13h / Night FDP (WOCL) ≤10h validated at pairing build",
                  "168-hour rolling rest cycle (new Nov 2025 weekly rest rule)",
                  "Maximum 2 consecutive night duties per 7-day period",
                  "FDP extension ban during split duty (Jan 2024 rule)",
                  "DGCA SDMS occurrence report format and digital submission",
                  "Indian aviation calendar and multi-base SME architecture (5–50 aircraft)",
                ].map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={13} className="text-green-400 shrink-0" /> {r}
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#0c1929]/40 border border-green-500/15 p-6">
              <div className="text-[10px] text-green-400 marketing-font-mono uppercase tracking-widest font-bold mb-5">DGCA Audit Readiness</div>
              {[
                { label: "FDTL compliance rate", value: "100%", color: "bg-green-500" },
                { label: "FRMS evidence completeness", value: "94%", color: "bg-green-400" },
                { label: "SMS occurrence reporting", value: "100%", color: "bg-green-500" },
                { label: "Audit log completeness", value: "100%", color: "bg-green-500" },
                { label: "Documentation version control", value: "100%", color: "bg-green-500" },
              ].map((item, i) => (
                <div key={i} className="mb-3">
                  <div className="flex justify-between mb-1">
                    <span className="text-[11px] text-slate-500">{item.label}</span>
                    <span className="text-[11px] font-bold text-white marketing-font-mono">{item.value}</span>
                  </div>
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full ${item.color} rounded-full`} style={{ width: item.value }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 border-t border-white/5 bg-[#04090f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-3xl font-black marketing-font-heading mb-4">Ready for your DGCA audit?</h2>
          <p className="text-slate-400 max-w-md mx-auto mb-8">We'll show you the FRMS Phase 1 setup and what the Phase 2 progression looks like for your fleet.</p>
          <Link to="/marketing/demo" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-9 py-4 transition-all hover:-translate-y-0.5 shadow-xl shadow-orange-500/20">
            Request Demo <ArrowRight size={17} />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
